import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Bell,
  BookOpen,
  Calendar,
  Clock3,
  ChevronDown,
  Library,
  LogOut,
  Plus,
  Save,
  Star,
  Trash2,
} from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import CommunityBoxes from '../components/CommunityBoxes';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DAYS = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];
function buildDefaultWeek() {
  return Object.fromEntries(DAYS.map((day) => [day, { subjects: [] }]));
}

function getProgressEntry(progress, day, subject) {
  const entry = progress?.[day]?.[subject];
  if (entry && typeof entry === 'object') {
    return {
      completed: Boolean(entry.completed),
      questions_solved: entry.questions_solved || '',
    };
  }
  return {
    completed: Boolean(entry),
    questions_solved: '',
  };
}

function normalizeLibraryResource(item, index) {
  return {
    _id: item?._id || `temp-${index}-${Date.now()}`,
    title: item?.title || '',
    url: item?.url || '',
  };
}

function formatDateTime(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString('tr-TR');
}

function PanelCard({ icon: Icon, title, description, children, className = '', isOpen, onToggle }) {
  return (
    <section className={`rounded-2xl border border-white/10 bg-[#0A0A0A] p-6 ${className}`}>
      <button type="button" onClick={onToggle} className="flex w-full items-start justify-between gap-4 text-left">
        <div className="flex items-start gap-3">
          <div className="mt-1 rounded-xl bg-[#B026FF]/15 p-2 text-[#C966FF]">
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-xl font-black text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {title}
            </h3>
            {description && <p className="mt-1 text-sm text-[#A1A1AA]">{description}</p>}
          </div>
        </div>
        <ChevronDown className={`mt-1 h-5 w-5 shrink-0 text-[#A1A1AA] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && <div className="mt-6">{children}</div>}
    </section>
  );
}

function PanelToggleCard({ icon: Icon, title, description, isActive, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-2xl border p-5 text-left transition-all ${
        isActive
          ? 'border-[#B026FF] bg-[#120818] shadow-[0_0_18px_rgba(176,38,255,0.18)]'
          : 'border-white/10 bg-[#0A0A0A] hover:border-[#B026FF]/40'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="mt-1 rounded-xl bg-[#B026FF]/15 p-2 text-[#C966FF]">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <h3 className="text-lg font-black text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {title}
          </h3>
          <p className="mt-1 text-sm text-[#A1A1AA]">{description}</p>
        </div>
      </div>
    </button>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [schedule, setSchedule] = useState(null);
  const [progress, setProgress] = useState({});
  const [scheduleHistory, setScheduleHistory] = useState([]);
  const [openHistoryItems, setOpenHistoryItems] = useState({});
  const [currentScheduleCreatedAt, setCurrentScheduleCreatedAt] = useState('');
  const [libraryResources, setLibraryResources] = useState([]);
  const [packageExpired, setPackageExpired] = useState(false);
  const [accountPending, setAccountPending] = useState(Boolean(user?.approved === false));
  const [loading, setLoading] = useState(true);

  const [mentors, setMentors] = useState([]);
  const [selectedMentorId, setSelectedMentorId] = useState('');
  const [ratingDialog, setRatingDialog] = useState(false);
  const [myRating, setMyRating] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [mentorRatings, setMentorRatings] = useState({ average: 0, total: 0 });

  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [siteSettings, setSiteSettings] = useState({});
  const [savingQuestionKey, setSavingQuestionKey] = useState('');
  const [savingResourceKey, setSavingResourceKey] = useState('');
  const [activeSection, setActiveSection] = useState('weekly');
  const [expandedDays, setExpandedDays] = useState(() => Object.fromEntries(DAYS.map((day, index) => [day, index === 0])));

  const unreadNotificationCount = useMemo(
    () => notifications.filter((notification) => !notification.read).length,
    [notifications]
  );

  const todayIndex = useMemo(() => {
    const day = new Date().getDay();
    return day === 0 ? 6 : day - 1;
  }, []);

  const fetchSchedule = useCallback(async () => {
    const { data } = await axios.get(`${API}/schedules/my-schedule`, { withCredentials: true });
    setSchedule(data.week_data || buildDefaultWeek());
    setProgress(data.progress || {});
    setCurrentScheduleCreatedAt(data.current_schedule_created_at || '');
    setScheduleHistory(Array.isArray(data.schedule_history) ? data.schedule_history : []);
    setOpenHistoryItems({});
    setLibraryResources(
      Array.isArray(data.library_resources)
        ? data.library_resources.map((item, index) => normalizeLibraryResource(item, index))
        : []
    );
    setPackageExpired(Boolean(data.package_expired));
    setAccountPending(Boolean(data.account_pending));
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/my-notifications`, { withCredentials: true });
      setNotifications(data);
    } catch (error) {
      console.error('User notifications fetch error:', error);
    }
  }, []);

  const fetchSiteSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/site-settings`);
      setSiteSettings(data || {});
    } catch (error) {
      console.error('Site ayarlari alinamadi:', error);
    }
  }, []);

  const fetchMentors = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/mentors`, { withCredentials: true });
      setMentors(data);
      const defaultMentorId =
        user?.mentor_id && data.some((mentor) => mentor._id === user.mentor_id)
          ? user.mentor_id
          : data[0]?._id || '';
      setSelectedMentorId(defaultMentorId);
    } catch (error) {
      console.error('Mentor listesi alinamadi:', error);
    }
  }, [user?.mentor_id]);

  const fetchMentorInfo = useCallback(async (mentorId) => {
    try {
      const [ratingsRes, myRatingRes] = await Promise.all([
        axios.get(`${API}/mentor/${mentorId}/ratings`),
        axios.get(`${API}/my-mentor-rating/${mentorId}`, { withCredentials: true }),
      ]);
      setMentorRatings({
        average: ratingsRes.data.average || 0,
        total: ratingsRes.data.total || 0,
      });
      setMyRating(myRatingRes.data?.rating || 0);
      setRatingComment(myRatingRes.data?.comment || '');
    } catch (error) {
      console.error('Mentor bilgisi alinamadi:', error);
    }
  }, []);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([fetchSchedule(), fetchNotifications(), fetchMentors(), fetchSiteSettings()]);
    } finally {
      setLoading(false);
    }
  }, [fetchMentors, fetchNotifications, fetchSchedule, fetchSiteSettings]);

  useEffect(() => {
    if (user?.role === 'admin') {
      navigate('/admin');
      return;
    }
    if (user?.role === 'mentor') {
      navigate('/mentor');
      return;
    }
    if (!user || user === false) {
      return;
    }

    loadDashboard();
  }, [loadDashboard, navigate, user]);

  useEffect(() => {
    if (selectedMentorId) {
      fetchMentorInfo(selectedMentorId);
    }
  }, [fetchMentorInfo, selectedMentorId]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const updateSubjectProgress = async (day, subject, changes) => {
    const previousEntry = getProgressEntry(progress, day, subject);
    const nextEntry = { ...previousEntry, ...changes };

    setProgress((prev) => ({
      ...prev,
      [day]: {
        ...(prev[day] || {}),
        [subject]: nextEntry,
      },
    }));

    try {
      await axios.put(
        `${API}/schedules/my-progress`,
        { day, subject, completed: nextEntry.completed, questions_solved: nextEntry.questions_solved },
        { withCredentials: true }
      );
    } catch (error) {
      setProgress((prev) => ({
        ...prev,
        [day]: {
          ...(prev[day] || {}),
          [subject]: previousEntry,
        },
      }));
      toast.error('Gorev durumu kaydedilemedi.');
    }
  };

  const toggleSubjectProgress = async (day, subject, checked) => {
    await updateSubjectProgress(day, subject, { completed: Boolean(checked) });
  };

  const saveQuestionProgress = async (day, subject) => {
    const questionKey = `${day}__${subject}`;
    const currentEntry = getProgressEntry(progress, day, subject);
    setSavingQuestionKey(questionKey);
    try {
      await updateSubjectProgress(day, subject, { questions_solved: currentEntry.questions_solved });
      toast.success('Gunluk cozulen soru kaydedildi.');
    } finally {
      setSavingQuestionKey('');
    }
  };

  const addLibraryResource = () => {
    setLibraryResources((prev) => [
      ...prev,
      {
        _id: `temp-${Date.now()}-${prev.length}`,
        title: '',
        url: '',
      },
    ]);
  };

  const updateLibraryResourceField = (resourceId, field, value) => {
    setLibraryResources((prev) =>
      prev.map((item) => (item._id === resourceId ? { ...item, [field]: value } : item))
    );
  };

  const saveLibraryResource = async (resource) => {
    if (!resource.title.trim() && !resource.url.trim()) {
      toast.error('Kaynak adi veya baglanti girmen gerekiyor.');
      return;
    }

    setSavingResourceKey(resource._id);
    try {
      if (String(resource._id).startsWith('temp-')) {
        const { data } = await axios.post(
          `${API}/my-library-resources`,
          { title: resource.title, url: resource.url },
          { withCredentials: true }
        );
        setLibraryResources((prev) =>
          prev.map((item) => (item._id === resource._id ? normalizeLibraryResource(data, 0) : item))
        );
      } else {
        await axios.put(
          `${API}/my-library-resources/${resource._id}`,
          { title: resource.title, url: resource.url },
          { withCredentials: true }
        );
      }
      toast.success('Kaynak kaydedildi.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kaynak kaydedilemedi.');
    } finally {
      setSavingResourceKey('');
    }
  };

  const deleteLibraryResource = async (resource) => {
    if (String(resource._id).startsWith('temp-')) {
      setLibraryResources((prev) => prev.filter((item) => item._id !== resource._id));
      return;
    }

    setSavingResourceKey(resource._id);
    try {
      await axios.delete(`${API}/my-library-resources/${resource._id}`, { withCredentials: true });
      setLibraryResources((prev) => prev.filter((item) => item._id !== resource._id));
      toast.success('Kaynak silindi.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kaynak silinemedi.');
    } finally {
      setSavingResourceKey('');
    }
  };

  const submitRating = async () => {
    if (accountPending) {
      toast.error('Hesabiniz onaylanmadan mentor degerlendirmesi yapamazsiniz.');
      return;
    }
    if (!selectedMentorId) {
      toast.error('Size atanmis mentor bulunmuyor.');
      return;
    }
    if (!myRating) {
      toast.error('Lutfen puan verin.');
      return;
    }

    try {
      await axios.post(
        `${API}/rate-mentor`,
        { mentor_id: selectedMentorId, rating: myRating, comment: ratingComment },
        { withCredentials: true }
      );
      toast.success('Puanlama kaydedildi.');
      setRatingDialog(false);
      fetchMentorInfo(selectedMentorId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Puanlama kaydedilemedi.');
    }
  };

  const handleNotificationDialogChange = async (open) => {
    setNotifOpen(open);
    if (open && unreadNotificationCount > 0) {
      try {
        await axios.put(`${API}/my-notifications/read-all`, {}, { withCredentials: true });
        setNotifications((prev) => prev.map((notification) => ({ ...notification, read: true })));
      } catch (error) {
        console.error('Bildirim sifirlama hatasi:', error);
      }
    }
  };

  const toggleExpandedDay = (day) => {
    setExpandedDays((prev) => ({
      ...prev,
      [day]: !prev[day],
    }));
  };

  const toggleHistoryItem = (historyKey) => {
    setOpenHistoryItems((prev) => ({
      ...prev,
      [historyKey]: !prev[historyKey],
    }));
  };

  const dashboardLocked = packageExpired || accountPending;

  return (
    <div
      className="min-h-screen bg-[#050505] text-white"
      style={{
        backgroundImage:
          'url(https://static.prod-images.emergentagent.com/jobs/d9bd63b7-d6a6-43cd-a29b-1a59dd534490/images/c3105e8d92164c6231220daeec06a2f7c7b1874bcf2946d5396ef6c5007e6abf.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
    >
      <div className="absolute inset-0 bg-black/70" />

      <header className="relative z-10 border-b border-white/10 bg-black/60 backdrop-blur-xl">
        <div className="container mx-auto flex items-center justify-between px-6 py-3">
          <Link to="/" className="flex items-center pl-1 transition-opacity hover:opacity-90">
            <span className="text-[1.7rem] font-black tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              KARGA AKADEMI
            </span>
          </Link>

          <div className="flex items-center gap-3 sm:gap-4">
            <span className="hidden text-sm text-[#A1A1AA] lg:inline">Hos geldin, {user?.name}</span>

            <Button
              onClick={() => handleNotificationDialogChange(true)}
              variant="outline"
              className="relative border-white/10 text-white hover:border-[#B026FF]/50 hover:text-[#B026FF]"
            >
              <Bell className="h-4 w-4" />
              {unreadNotificationCount > 0 && (
                <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs text-white">
                  {unreadNotificationCount}
                </span>
              )}
            </Button>

            {mentors.length > 0 && !accountPending && (
              <Button
                onClick={() => setRatingDialog(true)}
                variant="outline"
                className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
              >
                <Star className="mr-2 h-4 w-4" /> Mentoru Puanla
              </Button>
            )}

            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-white/10 text-white hover:border-[#B026FF]/50 hover:text-[#B026FF]"
            >
              <LogOut className="mr-2 h-4 w-4" /> Cikis
            </Button>
          </div>
        </div>
      </header>

      <main className="relative z-10 container mx-auto px-6 py-12" data-testid="dashboard">
        <div className="mb-12">
          <div className="mb-4 flex items-center gap-3">
            <Calendar className="h-8 w-8 text-[#B026FF]" strokeWidth={1.5} />
            <h2 className="text-3xl font-black tracking-tight sm:text-4xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Kullanici Paneli
            </h2>
          </div>
          <p className="text-[#A1A1AA]">
            Haftalik cizelgeni takip et, gunluk cozumlerini kaydet ve kendi kutuphane kaynaklarini olustur.
          </p>
        </div>

        {packageExpired && (
          <div className="mb-8 flex items-center gap-4 rounded-lg border border-red-500/30 bg-red-500/10 p-6">
            <AlertTriangle className="h-8 w-8 flex-shrink-0 text-red-400" />
            <div>
              <h3 className="text-lg font-bold text-red-400" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Paket Suren Dolmus
              </h3>
              <p className="text-sm text-red-300/80">
                Cizelgeni goruntulemeye devam etmek icin lutfen paketini yenile.
              </p>
              <Button onClick={() => navigate('/packages')} className="mt-3 bg-red-500 text-white hover:bg-red-600">
                Paketleri Goruntule
              </Button>
            </div>
          </div>
        )}

        {accountPending && (
          <div className="mb-8 flex items-center gap-4 rounded-lg border border-[#B026FF]/30 bg-[#B026FF]/10 p-6">
            <AlertTriangle className="h-8 w-8 flex-shrink-0 text-[#C966FF]" />
            <div>
              <h3 className="text-lg font-bold text-[#E8C8FF]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Hesabiniz Onay Bekliyor
              </h3>
              <p className="max-w-3xl text-sm leading-relaxed text-[#E9D5FF]">
                Degerli ziyaretcimiz sitemize kayit oldugunuz icin tesekkur ederiz. Karga Akademi tarafindan hesabiniz
                onaylandiktan sonra uyelik girisiniz acilacaktir.
              </p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="py-20 text-center">
            <div className="text-xl text-[#B026FF]">Yukleniyor...</div>
          </div>
        ) : (
          <>
            <div className={dashboardLocked ? 'blur-sm pointer-events-none select-none' : ''}>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <PanelToggleCard
                  icon={Calendar}
                  title="Haftalik Cizelge"
                  description="Cizelgeni takip et"
                  isActive={activeSection === 'weekly'}
                  onClick={() => setActiveSection('weekly')}
                />
                <PanelToggleCard
                  icon={BookOpen}
                  title="Gunluk Cozulen Soru"
                  description="Gunluk not ve soru gir"
                  isActive={activeSection === 'questions'}
                  onClick={() => setActiveSection('questions')}
                />
                <PanelToggleCard
                  icon={Library}
                  title="Kullanici Kutuphanesi / Kaynaklari"
                  description="Kaynak kutularini yonet"
                  isActive={activeSection === 'library'}
                  onClick={() => setActiveSection('library')}
                />
                <PanelToggleCard
                  icon={Clock3}
                  title="Gecmis Cizelgeler"
                  description="Eski haftalari goruntule"
                  isActive={activeSection === 'history'}
                  onClick={() => setActiveSection('history')}
                />
              </div>

              <div className="mt-6">
                {activeSection === 'weekly' && (
                  <PanelCard
                    icon={Calendar}
                    title="Aktif Cizelge"
                    description={`Guncel calisma planin burada. Olusturma tarihi: ${formatDateTime(currentScheduleCreatedAt)}`}
                    isOpen
                  >
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {DAYS.map((day, index) => {
                      const dayData = schedule?.[day] || { subjects: [] };
                      const isToday = index === todayIndex;

                      return (
                        <motion.div
                          key={`schedule-${day}`}
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.04 }}
                          className={`rounded-xl border p-4 ${
                            isToday
                              ? 'border-[#B026FF] bg-[#120818]'
                              : 'border-white/10 bg-black/30'
                          }`}
                        >
                          <div className="mb-3">
                            <h4 className="font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                              {day}
                            </h4>
                            {isToday && <span className="text-xs font-semibold text-[#B026FF]">Bugun</span>}
                          </div>

                          <div className="space-y-2">
                            {dayData.subjects?.length > 0 ? (
                              dayData.subjects.map((subject, subjectIndex) => {
                                const progressEntry = getProgressEntry(progress, day, subject);
                                const checked = progressEntry.completed;
                                return (
                                  <label
                                    key={`weekly-${day}-${subjectIndex}-${subject}`}
                                    className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2 text-sm ${
                                      checked
                                        ? 'border-green-500/30 bg-green-500/10 text-green-100'
                                        : 'border-white/10 bg-white/5 text-[#E0E0E0]'
                                    }`}
                                  >
                                    <Checkbox
                                      checked={checked}
                                      disabled={dashboardLocked}
                                      onCheckedChange={(value) => toggleSubjectProgress(day, subject, value)}
                                      className="mt-0.5 border-white/30 data-[state=checked]:border-green-500 data-[state=checked]:bg-green-500"
                                    />
                                    <span className={checked ? 'line-through text-green-200/90' : ''}>{subject}</span>
                                  </label>
                                );
                              })
                            ) : (
                              <p className="text-sm italic text-[#71717A]">Bu gun icin cizelge eklenmedi.</p>
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                  </PanelCard>
                )}

                {activeSection === 'questions' && (
                  <PanelCard
                    icon={BookOpen}
                    title="Gunluk Cozulen Soru"
                    description="Cizelgedeki gun ve ders maddelerine gore soru adedi veya notunu kaydet."
                    isOpen
                  >
                  <div className="space-y-4 max-h-[980px] overflow-y-auto pr-1">
                    {DAYS.map((day, index) => {
                      const dayData = schedule?.[day] || { subjects: [] };
                      return (
                        <motion.div
                          key={`question-${day}`}
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.04 }}
                          className="rounded-xl border border-white/10 bg-black/30 p-4"
                        >
                          <h4 className="mb-3 font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            <button
                              type="button"
                              onClick={() => toggleExpandedDay(day)}
                              className="flex w-full items-center justify-between text-left"
                            >
                              <span>{day}</span>
                              <ChevronDown className={`h-4 w-4 text-[#A1A1AA] transition-transform ${expandedDays[day] ? 'rotate-180' : ''}`} />
                            </button>
                          </h4>
                          {expandedDays[day] && <div className="space-y-3">
                            {dayData.subjects?.length > 0 ? (
                              dayData.subjects.map((subject, subjectIndex) => {
                                const progressEntry = getProgressEntry(progress, day, subject);
                                const questionKey = `${day}__${subject}`;
                                return (
                                  <div
                                    key={`questions-${day}-${subjectIndex}-${subject}`}
                                    className="rounded-lg border border-white/10 bg-white/5 p-3"
                                  >
                                    <p className="mb-2 text-sm font-semibold text-white">{subject}</p>
                                    <div className="flex items-center gap-2">
                                      <Input
                                        value={progressEntry.questions_solved}
                                        disabled={dashboardLocked}
                                        onChange={(event) =>
                                          setProgress((prev) => ({
                                            ...prev,
                                            [day]: {
                                              ...(prev[day] || {}),
                                              [subject]: {
                                                ...getProgressEntry(prev, day, subject),
                                                questions_solved: event.target.value,
                                              },
                                            },
                                          }))
                                        }
                                        onKeyDown={(event) => {
                                          if (event.key === 'Enter') {
                                            event.preventDefault();
                                            saveQuestionProgress(day, subject);
                                          }
                                        }}
                                        placeholder="Ornek: 45 soru / video 2 tamamlandi"
                                        className="h-10 border-white/10 bg-black/40 text-sm text-white"
                                      />
                                      <Button
                                        type="button"
                                        size="sm"
                                        disabled={dashboardLocked || savingQuestionKey === questionKey}
                                        onClick={() => saveQuestionProgress(day, subject)}
                                        className="h-10 shrink-0 bg-[#B026FF] px-3 text-white hover:bg-[#C966FF]"
                                      >
                                        <Save className="mr-1 h-3.5 w-3.5" />
                                        {savingQuestionKey === questionKey ? '...' : 'Kaydet'}
                                      </Button>
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <p className="text-sm italic text-[#71717A]">Bu gun icin soru girilecek ders yok.</p>
                            )}
                          </div>}
                        </motion.div>
                      );
                    })}
                  </div>
                  </PanelCard>
                )}

                {activeSection === 'library' && (
                  <PanelCard
                    icon={Library}
                    title="Kullanici Kutuphanesi / Kaynaklari"
                    description="Kendi kutuphane ismini, notunu veya baglantini istedigin kadar kutu ekleyerek saklayabilirsin."
                    isOpen
                  >
                  <div className="mb-4 flex justify-end">
                    <Button
                      type="button"
                      onClick={addLibraryResource}
                      disabled={dashboardLocked}
                      variant="outline"
                      className="border-[#B026FF]/50 text-[#B026FF] hover:bg-[#B026FF]/10"
                    >
                      <Plus className="mr-2 h-4 w-4" /> Yeni Kaynak Kutusu
                    </Button>
                  </div>

                  <div className="space-y-4 max-h-[980px] overflow-y-auto pr-1">
                    {libraryResources.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-white/10 bg-black/20 p-5 text-sm text-[#71717A]">
                        Henuz kaynak kutusu eklenmedi. Yeni kutu olusturup istedigin kadar kaynak birakabilirsin.
                      </div>
                    ) : (
                      libraryResources.map((resource, index) => (
                        <div key={resource._id} className="rounded-xl border border-white/10 bg-black/30 p-4">
                          <div className="mb-3 flex items-center justify-between">
                            <h4 className="font-semibold text-white">Kaynak Kutusu {index + 1}</h4>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={dashboardLocked || savingResourceKey === resource._id}
                              onClick={() => deleteLibraryResource(resource)}
                              className="border-red-500/40 text-red-400 hover:bg-red-500/10"
                            >
                              <Trash2 className="mr-1 h-3.5 w-3.5" /> Sil
                            </Button>
                          </div>

                          <div className="space-y-3">
                            <Input
                              value={resource.title}
                              disabled={dashboardLocked}
                              onChange={(event) => updateLibraryResourceField(resource._id, 'title', event.target.value)}
                              placeholder="Kaynak veya kutuphane adi"
                              className="border-white/10 bg-black/40 text-white"
                            />
                            <Input
                              value={resource.url}
                              disabled={dashboardLocked}
                              onChange={(event) => updateLibraryResourceField(resource._id, 'url', event.target.value)}
                              placeholder="https://... veya not"
                              className="border-white/10 bg-black/40 text-white"
                            />
                            <div className="flex justify-end">
                              <Button
                                type="button"
                                size="sm"
                                disabled={dashboardLocked || savingResourceKey === resource._id}
                                onClick={() => saveLibraryResource(resource)}
                                className="bg-[#B026FF] text-white hover:bg-[#C966FF]"
                              >
                                <Save className="mr-1 h-3.5 w-3.5" />
                                {savingResourceKey === resource._id ? 'Kaydediliyor...' : 'Kaydet'}
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  </PanelCard>
                )}

                {activeSection === 'history' && (
                  <PanelCard
                    icon={Clock3}
                    title="Gecmis Cizelgeler"
                    description="Mentorunun onceki haftalarda hazirladigi cizelgeleri burada gorebilirsin."
                    isOpen
                  >
                    <div className="space-y-4">
                      {scheduleHistory.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-white/10 bg-black/20 p-5 text-sm text-[#71717A]">
                          Henuz gecmise dusen bir cizelge bulunmuyor.
                        </div>
                      ) : (
                        scheduleHistory.map((historyItem, historyIndex) => {
                          const historyKey = historyItem._id || `${historyItem.schedule_version}-${historyIndex}`;
                          const isOpen = Boolean(openHistoryItems[historyKey]);
                          return (
                            <div key={historyKey} className="rounded-xl border border-white/10 bg-black/30">
                              <button
                                type="button"
                                onClick={() => toggleHistoryItem(historyKey)}
                                className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left"
                              >
                                <div>
                                  <h4 className="font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                    Gecmis Cizelge #{historyItem.schedule_version || historyIndex + 1}
                                  </h4>
                                  <p className="text-xs text-[#A1A1AA]">
                                    Olusturma: {formatDateTime(historyItem.created_at)} | Gecmise dusme: {formatDateTime(historyItem.archived_at)}
                                  </p>
                                </div>
                                <ChevronDown className={`h-4 w-4 text-[#A1A1AA] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                              </button>
                              {isOpen && (
                                <div className="border-t border-white/10 px-4 pb-4 pt-4">
                                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                    {DAYS.map((day) => {
                                      const dayData = historyItem.week_data?.[day] || { subjects: [] };
                                      return (
                                        <div key={`${historyKey}-${day}`} className="rounded-lg border border-white/10 bg-white/5 p-3">
                                          <h5 className="mb-2 font-semibold text-white">{day}</h5>
                                          {dayData.subjects?.length > 0 ? (
                                            <div className="space-y-2">
                                              {dayData.subjects.map((subject, subjectIndex) => (
                                                <div key={`${historyKey}-${day}-${subjectIndex}`} className="rounded-md border border-white/10 bg-black/30 px-3 py-2 text-sm text-[#D4D4D8]">
                                                  {subject}
                                                </div>
                                              ))}
                                            </div>
                                          ) : (
                                            <p className="text-sm italic text-[#71717A]">Bu gun icin konu girilmemis.</p>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </PanelCard>
                )}
              </div>
            </div>

            <div className="mt-10">
              <div className="relative">
                <div className={accountPending ? 'pointer-events-none select-none blur-sm' : ''}>
                  <CommunityBoxes boxes={siteSettings.community_boxes} title="Topluluk" />
                </div>
                {accountPending &&
                  Array.isArray(siteSettings.community_boxes) &&
                  siteSettings.community_boxes.length > 0 && (
                    <div className="absolute inset-0 flex items-center justify-center rounded-2xl border border-white/10 bg-black/20 px-6 text-center">
                      <div className="max-w-md">
                        <p className="text-sm font-semibold text-white">Topluluk alani henuz kapali</p>
                        <p className="mt-2 text-xs text-[#D4D4D8]">
                          Hesabiniz onaylandiktan sonra WhatsApp topluluk baglantilari burada aktif olarak acilacaktir.
                        </p>
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </>
        )}
      </main>

      <Dialog open={ratingDialog} onOpenChange={setRatingDialog}>
        <DialogContent className="border-white/10 bg-[#0A0A0A] text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Mentorunu Puanla
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Puan verecegin mentoru sec ve degerlendirmeni kaydet.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <p className="text-sm text-[#E0E0E0]">Atanmis mentor</p>
              <Select value={selectedMentorId} onValueChange={setSelectedMentorId}>
                <SelectTrigger className="border-white/10 bg-black/40 text-white">
                  <SelectValue placeholder="Atanmis mentor yok" />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-[#0A0A0A]">
                  {mentors.map((mentor) => (
                    <SelectItem key={mentor._id} value={mentor._id} className="text-white">
                      {mentor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setMyRating(star)}
                  className="transition-transform hover:scale-110"
                >
                  <Star
                    className={`h-10 w-10 ${
                      star <= myRating ? 'fill-yellow-400 text-yellow-400' : 'text-[#71717A]'
                    }`}
                  />
                </button>
              ))}
            </div>

            <p className="text-center text-sm text-[#A1A1AA]">
              Mevcut ortalama: {mentorRatings.average}/5 ({mentorRatings.total} degerlendirme)
            </p>

            <Textarea
              value={ratingComment}
              onChange={(event) => setRatingComment(event.target.value)}
              placeholder="Yorum yazin (istege bagli)"
              className="border-white/10 bg-black/40 text-white"
            />
          </div>

          <DialogFooter>
            <Button onClick={() => setRatingDialog(false)} variant="outline" className="border-white/10 text-white">
              Iptal
            </Button>
            <Button
              onClick={submitRating}
              disabled={accountPending || !selectedMentorId}
              className="bg-[#B026FF] text-white hover:bg-[#C966FF] disabled:opacity-50"
            >
              Puanla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={notifOpen} onOpenChange={handleNotificationDialogChange}>
        <DialogContent className="border-white/10 bg-[#0A0A0A] text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Bildirimler
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Bildirimleri actiginda okunmamis sayac otomatik sifirlanir.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[420px] space-y-3 overflow-y-auto py-2">
            {notifications.length === 0 ? (
              <p className="py-4 text-center text-[#71717A]">Bildirim yok.</p>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification._id}
                  className={`rounded-lg border p-3 ${
                    notification.read ? 'border-white/5 opacity-60' : 'border-[#B026FF]/30 bg-[#B026FF]/5'
                  }`}
                >
                  <p className="text-sm text-[#E0E0E0]">{notification.message}</p>
                  <span className="text-xs text-[#71717A]">
                    {notification.created_at ? new Date(notification.created_at).toLocaleDateString('tr-TR') : '-'}
                  </span>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
